import { useMemo, useState } from "react";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { rankItems } from "@/mocks/digData";

type RankTab = "热度总榜" | "飙升榜" | "降温榜";

const tabs: RankTab[] = ["热度总榜", "飙升榜", "降温榜"];

const trendMeta: Record<string, { icon: string; className: string; label: string }> = {
  up: { icon: "ri-arrow-up-line", className: "text-primary-600", label: "上升" },
  down: { icon: "ri-arrow-down-line", className: "text-accent-600", label: "回落" },
  flat: { icon: "ri-subtract-line", className: "text-foreground-500", label: "持平" },
};

const maxHeat = Math.max(...rankItems.map((item) => item.heat));

export default function RankSection() {
  const [tab, setTab] = useState<RankTab>("热度总榜");

  const list = useMemo(() => {
    if (tab === "飙升榜") {
      return rankItems
        .filter((item) => item.trend === "up")
        .sort((a, b) => b.heat - a.heat)
        .map((item, index) => ({ ...item, rank: index + 1 }));
    }
    if (tab === "降温榜") {
      return rankItems
        .filter((item) => item.trend === "down")
        .sort((a, b) => b.heat - a.heat)
        .map((item, index) => ({ ...item, rank: index + 1 }));
    }
    return [...rankItems].sort((a, b) => b.heat - a.heat).map((item, index) => ({ ...item, rank: index + 1 }));
  }, [tab]);

  return (
    <section id="rank" className="scroll-mt-24 py-16 md:py-24 bg-background-100">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading
            eyebrow="NEW MELON RANK"
            title="新瓜榜 · 新瓜出现就看这里"
            desc="按实时热度排列的追踪清单，附带趋势方向与一句话看点，帮你在最短时间里决定先看哪一集。"
          />
          <div className="inline-flex items-center gap-1 p-1 rounded-full border border-background-300 bg-background-50">
            {tabs.map((item) => {
              const isActive = tab === item;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => setTab(item)}
                  className={`px-3 md:px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:text-primary-700"
                  }`}
                >
                  {item}
                </button>
              );
            })}
          </div>
        </div>

        <ol className="mt-10 md:mt-14 flex flex-col gap-3 md:gap-4">
          {list.map((item) => (
            <li
              key={item.id}
              className="group flex items-center gap-3 md:gap-5 rounded-lg border border-background-200 bg-background-50 p-3 md:p-4 hover:border-primary-300 transition-colors"
            >
              <span
                className={`shrink-0 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-md font-heading font-black text-lg md:text-xl ${
                  item.rank <= 3
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-200 text-foreground-700"
                }`}
              >
                {item.rank}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 truncate">
                    {item.name}
                  </h3>
                  <span className="px-2 py-0.5 rounded-md text-xs bg-secondary-100 text-secondary-900 whitespace-nowrap">
                    {item.category}
                  </span>
                </div>
                <p className="mt-1 text-xs md:text-sm text-foreground-600 truncate">{item.note}</p>
                <div className="mt-2 h-1.5 w-full max-w-xs rounded-full bg-background-200 overflow-hidden">
                  <span
                    className="block h-full rounded-full bg-primary-500"
                    style={{ width: `${Math.round((item.heat / maxHeat) * 100)}%` }}
                  />
                </div>
              </div>

              <div className="shrink-0 text-right">
                <p className="font-heading text-base md:text-lg font-bold text-foreground-900">
                  {item.heat}
                  <span className="text-xs font-normal text-foreground-500 ml-0.5">万</span>
                </p>
                <p
                  className={`mt-0.5 inline-flex items-center gap-1 text-xs ${trendMeta[item.trend].className}`}
                >
                  <i className={trendMeta[item.trend].icon} />
                  {trendMeta[item.trend].label}
                </p>
              </div>
            </li>
          ))}
        </ol>

        {list.length === 0 && (
          <p className="mt-10 text-center text-foreground-500">该榜暂时没有符合条件的事件。</p>
        )}
      </div>
    </section>
  );
}